import type { ApiErrorCode } from 'src/enums/api-error-code';
import type { HttpStatusCode } from 'src/enums/http-status-code';

export async function get<T>(urlPath: string) {
  const response = await fetch(`${getMobileBackendHostname()}/${urlPath}`);
  return handleResponse<T>(response);
}

export async function put<T>(urlPath: string, body?: any) {
  const response = await fetch(`${getMobileBackendHostname()}/${urlPath}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return handleResponse<T>(response);
}

export async function post<T>(urlPath: string, body: any) {
  const response = await fetch(`${getMobileBackendHostname()}/${urlPath}`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return handleResponse<T>(response);
}

export async function patch<T>(urlPath: string, body?: any) {
  const response = await fetch(`${getMobileBackendHostname()}/${urlPath}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return handleResponse<T>(response);
}

export async function del<T>(urlPath: string) {
  const response = await fetch(`${getMobileBackendHostname()}/${urlPath}`, {
    method: 'DELETE',
  });
  return handleResponse<T>(response);
}

export type ResponseError = {
  statusCode: number;
  response?: { errors?: ApiErrorModel[] };
};

export const isInstanceOfResponseError = (object: unknown): object is ResponseError => {
  return typeof object === 'object' && object !== null && 'statusCode' in object;
};

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status >= 200 && response.status < 300) {
    // This is needed otherwise the json call below will throw an error
    if (response.status === 204) {
      return null as T;
    }
    return response.json() as Promise<T>;
  }

  const responseJson = await (response.json() as Promise<ResponseError['response']>);
  const error: ResponseError = { statusCode: response.status, response: responseJson };
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw error;
}

export function getMobileBackendHostname() {
  return process.env.GATSBY_MOBILE_BACKEND_HOSTNAME;
}

export const parseExpectedErrorCodesOnThrow = <T>(
  networkCall: () => Promise<T>,
  expectedErrorCodes: string[]
): Promise<T> =>
  networkCall().catch((e) => {
    const responseErrors = isInstanceOfResponseError(e) ? (e.response?.errors ?? []) : [];
    const knownResponseErrorCodes = responseErrors
      .map((error: { code?: ApiErrorCode }) => error.code)
      .filter((code?: ApiErrorCode) => !!code)
      .filter((code: ApiErrorCode) => expectedErrorCodes.includes(code));

    if (knownResponseErrorCodes.length > 0) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw knownResponseErrorCodes[0];
    }

    throw e;
  });

export const containsError = (
  errorResponse: any,
  httpStatusCode: HttpStatusCode,
  errorCode: ApiErrorCode
): boolean =>
  (isInstanceOfResponseError(errorResponse) &&
    errorResponse.statusCode === httpStatusCode &&
    errorResponse.response?.errors?.some((error: ApiErrorModel) => error.code === errorCode)) ??
  false;

type ApiErrorModel = {
  error: string;
  code: ApiErrorCode;
  extraProperties: Record<string, string>;
};
